"use client"

import React, { useEffect } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import Image from "next/image"
import { useEventManagement } from "@/hooks/useEventManagement"
import { assets } from "@/utils/assets" 
import { useLocale } from "@/context/LocaleContext" 

interface EventDraft {
    summary?: Record<string, unknown>;
    payload?: {
        jenis_acara_id?: number;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export default function AddEvent() {
    const router = useRouter()
    const searchParams = useSearchParams()
    const idTema = searchParams.get('idtema') 

    const { eventTypes, fetchEventTypes, isLoadingList } = useEventManagement()
    const { t, locale } = useLocale() 

    useEffect(() => {
        fetchEventTypes()
    }, [fetchEventTypes])

    const handleEventTypeSelect = (eventTypeId: number) => {
        const localePrefix = locale === "en" ? "/en" : ""
        const draftStr = sessionStorage.getItem("eventDraft");
        
    
        let existingDraft: EventDraft = {};
        
        if (draftStr) {
            try { 
                existingDraft = JSON.parse(draftStr); 
            } catch (error) {
                console.error("Gagal membaca eventDraft sebelumnya:", error);
            }
        }
        
        const updatedDraft = {
            ...existingDraft,
            payload: {
                ...(existingDraft.payload || {}),
                jenis_acara_id: eventTypeId
            }
        };
        
        sessionStorage.setItem("eventDraft", JSON.stringify(updatedDraft));

        if (idTema) {
            router.push(`${localePrefix}/add-event/${eventTypeId}?idtema=${idTema}`)
        } else {
            router.push(`${localePrefix}/add-event/${eventTypeId}`)
        }
    }

    const getIconUrl = (iconPath?: string) => {
        if (!iconPath) return "/images/icons/acara-lainnya.svg";
        if (iconPath.startsWith("http")) return iconPath;
        const safePath = encodeURI(iconPath);
        return assets(safePath);
    }

    return (
        <div className="w-full bg-white">
            <div className="w-full pt-4 md:pt-6 pb-12">
                {/* HEADER SECTION */}
                <div className="mb-5">
                    <h1 className="text-[18px] md:text-[20px] font-bold text-gray-900 dark:text-white mb-8">
                        {t("AddEvent.title") || "Buat Undangan Digital"}
                    </h1>
                    <div className="flex flex-col space-y-1">
                        <h2 className="text-[14px] md:text-[15px] font-medium text-gray-900 dark:text-gray-100">
                            {t("AddEvent.subtitle") || "Pilih Jenis Acara"}
                        </h2>
                        <span className="text-[13px] text-gray-400 dark:text-gray-500 font-normal">
                            {t("AddEvent.step") || "Langkah 1 dari 5"}
                        </span>
                    </div>
                </div>

                <hr className="border-gray-100 dark:border-gray-800 mb-8" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {isLoadingList ? (
                        [1, 2, 3].map((skeleton) => (
                            <div key={skeleton} className="flex items-center justify-between p-5 md:p-6 bg-[#FCF9F9] rounded-[14px] animate-pulse h-[100px]">
                                <div className="flex-1 pr-2 space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                                    <div className="h-3 bg-gray-200 rounded w-4/5"></div>
                                </div>
                                <div className="w-[55px] h-[55px] bg-gray-200 rounded-lg flex-shrink-0"></div>
                            </div>
                        ))
                    ) : (
                        eventTypes.map((eventType) => {
                            const resolvedIconUrl = getIconUrl(eventType.icon);
                            return(
                                <button
                                key={eventType.id}
                                onClick={() => handleEventTypeSelect(eventType.id)}
                                className="group flex items-center justify-between p-5 md:p-6 bg-[#FCF9F9] dark:bg-gray-800/50 rounded-[14px] transition-all duration-300 ease-out text-left w-full cursor-pointer hover:shadow-sm border border-transparent hover:border-red-200"
                            >
                                <div className="flex-1 pr-4">
                                    <h3 className="font-bold text-gray-900 dark:text-white text-[13px] md:text-[14px] mb-1.5 transition-colors group-hover:text-[#F15C61]">
                                        {eventType.jenis_acara}
                                    </h3>
                                    <p className="text-[11.5px] text-gray-400 dark:text-gray-500 leading-relaxed pr-2 line-clamp-2">
                                        {eventType.keterangan_acara || t("AddEvent.default_desc") || "Custom undangan untuk semua jenis acara"}
                                    </p>
                                </div>
                                
                                {/* Pembungkus Image yang memiliki posisi relative */}
                                <div className="relative w-[55px] h-[55px] md:w-[65px] md:h-[65px] flex-shrink-0 transition-transform duration-300 transform group-hover:scale-105">
                                    {/* Menggunakan next/image */}
                                    <Image 
                                        src={resolvedIconUrl} 
                                        alt={eventType.jenis_acara}
                                        fill
                                        sizes="(max-width: 768px) 55px, 65px"
                                        unoptimized
                                        className="object-contain drop-shadow-sm"
                                        onError={(e) => {
                                            const target = e.currentTarget as HTMLImageElement;
                                            if (!target.src.includes("acara-lainnya.svg")) {
                                                target.src = "/images/icons/acara-lainnya.svg";
                                                target.srcset = "";
                                            }
                                        }}
                                    />
                                </div>
                            </button>
                            )
                        })
                    )}
                </div>
            </div>
        </div>
    )
}